import type { KitErrorCode, KitErrorSource, KitMethod } from '../../appKit/host';

/**
 * Never `allow-same-origin`: the session token lives in localStorage, and a
 * same-origin frame running untrusted script could read it.
 */
export const SANDBOX_PERMISSIONS = 'allow-scripts allow-forms allow-modals allow-downloads';
/** A stranger's page gets no native dialogs: a browser prompt over our origin could pass for ours. */
export const UNTRUSTED_SANDBOX_PERMISSIONS = 'allow-scripts allow-forms allow-downloads';
export const FRAME_FEATURES = 'fullscreen; clipboard-write';

export const MAX_ERROR_CHARS = 500;
export const MAX_FIX_CHARS = 2000;
export const MAX_ERRORS = 20;
export const SNAPSHOT_TIMEOUT_MS = 2000;
/** Requests a page may have waiting at once; more are refused, so a loop cannot queue work. */
export const MAX_PENDING_REQUESTS = 8;

export interface HtmlSandboxError {
  readonly kind: string;
  readonly message: string;
  readonly source: KitErrorSource;
}

export interface SandboxRequestFailure {
  readonly method: string;
  readonly code: KitErrorCode;
  readonly message: string;
}

export interface CaptureOptions {
  /** Waits for the page to stop changing first, so one still drawing is not caught halfway. */
  readonly settle?: boolean;
}

export interface HtmlSandboxHandle {
  captureFrame: (options?: CaptureOptions) => Promise<Blob>;
  readonly loaded: boolean;
  /** Errors the page itself reported since this load; the kit's own are left out. */
  readonly pageErrors: readonly HtmlSandboxError[];
}

export interface SandboxReply {
  readonly value: unknown;
  readonly transfer?: Transferable[];
}

/** What a page may ask of the host, and the state it is told. Everything it sends is untrusted. */
export interface SandboxBridge {
  readonly state: unknown;
  /** Called synchronously on arrival, so a check of the user's gesture still sees it. */
  handle(method: KitMethod, params: unknown): Promise<SandboxReply>;
}

export class SandboxRequestError extends Error {
  readonly code: KitErrorCode;

  constructor(code: KitErrorCode, message: string) {
    super(message);
    this.name = 'SandboxRequestError';
    this.code = code;
  }
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const fixRequestText = (errors: readonly HtmlSandboxError[]): string => {
  const lines = errors.map((error) => '- ' + error.message).join('\n');
  return `The app's page shows these errors:\n${lines}`.slice(0, MAX_FIX_CHARS);
};
